import React, { useState, useEffect } from 'react';
import { Mail, Send, User, MessageSquare, AlertCircle, CheckCircle, Shield, RefreshCw } from 'lucide-react';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  return (
    <PageEditProvider slug="contact">
      <ContactInner />
      <PageEditToolbar />
    </PageEditProvider>
  );
}

function ContactInner() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '', // Honeypot field
    captcha_answer: ''
  });
  
  const [captcha, setCaptcha] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contact/captcha`);
      const data = await response.json();
      setCaptcha(data);
      setFormData(prev => ({ ...prev, captcha_answer: '' }));
    } catch (error) {
      console.error('Failed to load CAPTCHA:', error);
    }
    setCaptchaLoading(false);
  };

  useEffect(() => {
    fetchCaptcha();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    // Validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setStatus({ type: 'error', message: 'Please fill in all required fields.' });
      setLoading(false);
      return;
    }

    if (!formData.captcha_answer) {
      setStatus({ type: 'error', message: 'Please answer the security question.' });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/contact/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          captcha_answer: parseInt(formData.captcha_answer, 10)
        })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', message: data.message });
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          website: '',
          captcha_answer: ''
        });
        fetchCaptcha(); // Get new CAPTCHA
      } else {
        setStatus({ type: 'error', message: data.detail || 'Failed to send message.' });
        if (data.detail?.includes('CAPTCHA')) {
          fetchCaptcha();
        }
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Network error. Please try again.' });
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-black">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10" />
        <div className="max-w-4xl mx-auto px-6 py-20 relative">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            <PageEditable path="hero.pre" fallback="Contact" /> <span className="text-amber-400"><PageEditable path="hero.accent" fallback="Us" /></span>
          </h1>
          <PageEditable
            path="hero.subtitle"
            as="p"
            multiline
            className="text-xl text-zinc-400 max-w-2xl"
            fallback="Have questions about classes, performances, or collaborations? We'd love to hear from you!"
          />
        </div>
      </div>

      {/* Contact Form */}
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8">
          
          {/* Status Messages */}
          {status.message && (
            <div className={`mb-6 p-4 rounded-xl flex items-start gap-3 ${
              status.type === 'success' 
                ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                : 'bg-red-500/10 border border-red-500/30 text-red-400'
            }`}>
              {status.type === 'success' ? (
                <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              )}
              <p>{status.message}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <User className="w-4 h-4 inline mr-2" />
                Your Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your name"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500 transition-colors"
                required
                data-testid="contact-name-input"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Your Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500 transition-colors"
                required
                data-testid="contact-email-input"
              />
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Subject *
              </label>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-colors"
                required
                data-testid="contact-subject-select"
              >
                <option value="">Select a subject...</option>
                <option value="Classes & Lessons">Classes & Lessons</option>
                <option value="Performance Booking">Performance Booking</option>
                <option value="Orchestra Project / Funding">Orchestra Project / Funding</option>
                <option value="Membership Inquiry">Membership Inquiry</option>
                <option value="Cultural Programs">Cultural Programs</option>
                <option value="Collaboration">Collaboration</option>
                <option value="General Question">General Question</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <MessageSquare className="w-4 h-4 inline mr-2" />
                Your Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Write your message here..."
                rows={6}
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500 transition-colors resize-none"
                required
                data-testid="contact-message-textarea"
              />
            </div>

            {/* Honeypot - Hidden from users, bots will fill it */}
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="absolute -left-[9999px] opacity-0"
              tabIndex={-1}
              autoComplete="off"
            />

            {/* CAPTCHA */}
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
              <label className="block text-sm font-medium text-zinc-300 mb-3">
                <Shield className="w-4 h-4 inline mr-2" />
                Security Check *
              </label>
              
              {captcha ? (
                <div className="flex items-center gap-4">
                  <div className="bg-zinc-900 px-4 py-2 rounded-lg text-amber-400 font-mono text-lg">
                    {captcha.question}
                  </div>
                  <input
                    type="number"
                    name="captcha_answer"
                    value={formData.captcha_answer}
                    onChange={handleChange}
                    placeholder="?"
                    className="w-24 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-center focus:outline-none focus:border-amber-500 transition-colors"
                    required
                    data-testid="contact-captcha-input"
                  />
                  <button
                    type="button"
                    onClick={fetchCaptcha}
                    disabled={captchaLoading}
                    className="p-2 text-zinc-400 hover:text-amber-400 transition-colors"
                    title="Get new question"
                  >
                    <RefreshCw className={`w-5 h-5 ${captchaLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              ) : (
                <div className="text-zinc-500">Loading security check...</div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="contact-submit-btn"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send Message
                </>
              )}
            </button>
          </form>

          {/* Privacy Note */}
          <PageEditable
            path="privacyNote"
            as="p"
            multiline
            className="mt-6 text-sm text-zinc-500 text-center"
            fallback="Your information is protected and will only be used to respond to your inquiry."
          />
        </div>

        {/* Alternative Contact Info */}
        <div className="mt-12 text-center">
          <PageEditable
            path="altContact.title"
            as="h3"
            className="text-lg font-semibold text-white mb-4"
            fallback="Other Ways to Reach Us"
          />
          <div className="flex flex-col items-center gap-2 text-zinc-400">
            <p>
              <span className="text-amber-400"><PageEditable path="altContact.locationLabel" fallback="Location:" /></span> <PageEditable path="altContact.location" fallback="New Orleans, Louisiana" />
            </p>
            <PageEditable
              path="altContact.urgentNote"
              as="p"
              className="text-sm"
              fallback='For urgent matters, include "URGENT" in your subject line.'
            />
          </div>
        </div>
      </div>
    </div>
  );
}
